import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Group as GroupIcon,
  Storefront as StorefrontIcon,
  Info as InfoIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';

const SelectClient = ({ orderType }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, user } = useAuth();
  const orderId = searchParams.get('orderId');
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // El tipo de cliente se deriva del canal del pedido:
  // 'business' -> mayorista, 'public' -> minorista.
  const clientType = orderType === 'business' ? 'wholesale' : 'retail';
  const typeLabel = clientType === 'wholesale' ? 'Mayorista' : 'Minorista';

  useEffect(() => {
    if (user?.is_wholesale_client) {
      if (orderId) {
        navigate(`/orders/${orderId}/review`);
      } else {
        navigate('/orders/business');
      }
    }
  }, [user, navigate, orderId]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        is_active: 'true',
        client_type: clientType,
      });
      const response = await axios.get(`/api/clients/?${params.toString()}`);
      const data = response.data.results || response.data;
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = React.useCallback(() => {
    let filtered = clients;

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.name?.toLowerCase().includes(term) ||
          client.cuit?.toLowerCase().includes(term) ||
          client.phone?.toLowerCase().includes(term) ||
          client.email?.toLowerCase().includes(term)
      );
    }

    // El filtrado por tipo (mayorista/minorista) se resuelve en el backend
    // vía el parámetro client_type derivado del canal del pedido.

    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientType]);

  useEffect(() => {
    filterClients();
  }, [filterClients]);

  const handleSelectClient = async (clientId) => {
    if (orderId) {
      // Si hay un orderId, actualizar el pedido existente
      try {
        await axios.patch(`/api/orders/${orderId}/`, {
          client_id: parseInt(clientId),
        });
        // Volver a la página de revisión del pedido
        navigate(`/orders/${orderId}/review`);
      } catch (error) {
        console.error('Error updating order:', error);
        alert('Error al actualizar el cliente del pedido');
      }
    } else {
      // Si no hay orderId, crear un nuevo pedido según el tipo de orden
      navigate(`/orders/${orderType}?clientId=${clientId}`);
    }
  };

  const handleQuickSale = () => {
    navigate(`/orders/${orderType}`);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f6f7f8' }}>
      <Sidebar onLogout={handleLogout} />
      <Box sx={{ flex: 1, p: 3, pt: { xs: 7, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#4e7397', textTransform: 'none' }}
          >
            Inicio
          </Button>
          <Typography sx={{ color: '#4e7397' }}>/</Typography>
          <Button
            onClick={() => navigate('/orders')}
            sx={{ color: '#4e7397', textTransform: 'none' }}
          >
            Pedidos
          </Button>
          <Typography sx={{ color: '#4e7397' }}>/</Typography>
          <Typography sx={{ color: '#0e141b', fontWeight: 500 }}>Nuevo Pedido</Typography>
        </Box>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, color: '#0e141b', fontSize: { xs: '1.875rem', md: '2.25rem' } }}>
              Selección de Cliente
            </Typography>
            <Typography sx={{ color: '#4e7397' }}>
              Busque un cliente existente o registre uno nuevo para comenzar el pedido.
            </Typography>
          </Box>
          <Chip
            icon={<GroupIcon />}
            label="Paso 1 de 3"
            sx={{
              bgcolor: '#e3f2fd',
              color: '#1976d2',
              fontWeight: 'bold',
              px: 2,
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
          {/* Left Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Search */}
            <Paper sx={{ p: 3 }}>
              <Typography sx={{ fontWeight: 600, mb: 2, color: '#0e141b' }}>
                Buscar Cliente
              </Typography>
              <TextField
                fullWidth
                placeholder="Nombre, RUT, Cédula o Teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#4e7397' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                <Chip
                  label={`Mostrando clientes: ${typeLabel}`}
                  sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                />
              </Box>
            </Paper>

            {/* Results */}
            <Paper>
              <Box sx={{ p: 2.5, borderBottom: '1px solid #e7edf3', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GroupIcon sx={{ color: '#1976d2' }} />
                  <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>
                    Resultados de Búsqueda
                  </Typography>
                </Box>
                <Chip
                  label={`${filteredClients.length} encontrados`}
                  size="small"
                  sx={{ bgcolor: '#f5f5f5', color: '#4e7397' }}
                />
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem' }}>
                          Cliente
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem', display: { xs: 'none', sm: 'table-cell' } }}>
                          Contacto
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem', display: { xs: 'none', md: 'table-cell' } }}>
                          Dirección
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#4e7397', fontSize: '0.75rem' }}>
                          Acción
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredClients.map((client) => (
                        <TableRow
                          key={client.id}
                          hover
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: '#1976d2', width: 40, height: 40 }}>
                                {getInitials(client.name)}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontWeight: 600, color: '#0e141b' }}>
                                  {client.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: '#4caf50',
                                    }}
                                  />
                                  <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                    {client.is_wholesale ? 'Mayorista' : 'Minorista'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box>
                              <Typography sx={{ fontSize: '0.875rem', color: '#0e141b' }}>
                                {client.phone || 'N/A'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                                {client.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography sx={{ fontSize: '0.875rem', color: '#0e141b', fontWeight: 500 }}>
                              {client.delivery_address}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#4e7397' }}>
                              Localidad Tango: {client.delivery_city_name || client.city}
                            </Typography>
                            {client.is_wholesale && (
                              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {client.delivery_schedule?.routes?.length > 0 ? (
                                  client.delivery_schedule.routes.map((r, idx) => (
                                    <Chip
                                      key={idx}
                                      size="small"
                                      label={`Recorrido: ${r.day_label} (${r.route_name})`}
                                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                                    />
                                  ))
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Sin recorrido asignado"
                                    sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              onClick={() => handleSelectClient(client.id)}
                              sx={{
                                borderColor: '#1976d2',
                                color: '#1976d2',
                                '&:hover': {
                                  borderColor: '#1976d2',
                                  bgcolor: '#e3f2fd',
                                },
                              }}
                            >
                              Seleccionar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredClients.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography sx={{ color: '#4e7397' }}>
                              No se encontraron clientes
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>

          {/* Right Column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Quick Sale */}
            {orderType === 'public' && (
              <Paper sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#0e141b' }}>
                  Venta Rápida
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#4e7397', mb: 2 }}>
                  Utilice esta opción para ventas de mostrador sin registro de cliente específico.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<StorefrontIcon />}
                  onClick={handleQuickSale}
                  sx={{
                    borderColor: '#e7edf3',
                    color: '#0e141b',
                    '&:hover': {
                      borderColor: '#1976d2',
                      bgcolor: '#f5f5f5',
                    },
                  }}
                >
                  Seleccionar Consumidor Final
                </Button>
              </Paper>
            )}

            {/* Info */}
            <Paper sx={{ p: 2.5, bgcolor: '#e3f2fd', border: '1px solid #90caf9' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <InfoIcon sx={{ color: '#1976d2', mt: 0.5 }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1565c0', mb: 0.5, fontSize: '0.875rem' }}>
                    Consejo
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#1976d2' }}>
                    Puede buscar por nombre, apellido, razón social, RUT o número de teléfono.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SelectClient;
